import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const WELCOME_KEY = 'cloudassess_v3_welcomed';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const Dialog = styled.div`
  background: linear-gradient(160deg, #12163a 0%, #1a1f3a 100%);
  border: 1px solid #2d3561;
  border-radius: 20px;
  max-width: 640px;
  width: 100%;
  padding: 40px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(102, 126, 234, 0.15);
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 240px;
    height: 240px;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.12) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(102, 126, 234, 0.15);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.8em;
  color: #a5b4fc;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 1.85em;
  font-weight: 700;
  color: #e0e6ed;
  margin-bottom: 10px;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 1.5em;
  }
`;

const Subtitle = styled.p`
  color: #8892b0;
  font-size: 1em;
  line-height: 1.6;
  margin-bottom: 28px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 28px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: rgba(102, 126, 234, 0.06);
  border: 1px solid rgba(102, 126, 234, 0.15);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const FeatureIcon = styled.div`
  font-size: 1.4em;
  flex-shrink: 0;
  margin-top: 1px;
`;

const FeatureText = styled.div``;

const FeatureTitle = styled.div`
  font-weight: 600;
  font-size: 0.9em;
  color: #c4cbe8;
  margin-bottom: 3px;
`;

const FeatureDesc = styled.div`
  font-size: 0.82em;
  color: #6b7a9e;
  line-height: 1.4;
`;

const FrameworkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 32px;
`;

const FrameworkChip = styled.span`
  background: rgba(118, 75, 162, 0.15);
  border: 1px solid rgba(118, 75, 162, 0.3);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78em;
  color: #c4b5fd;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const StartButton = styled.button`
  flex: 1;
  min-width: 160px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: #4a5568;
  font-size: 0.88em;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: #8892b0;
  }
`;

const NoteRow = styled.div`
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #1e2444;
  font-size: 0.8em;
  color: #4a5568;
  text-align: center;
`;

const features = [
  {
    icon: '☁️',
    title: '7 Cloud Providers',
    desc: 'AWS, Azure, GCP, Alibaba, IBM, Oracle, Multi-Cloud'
  },
  {
    icon: '📋',
    title: '8 Compliance Frameworks',
    desc: 'NIST CSF 2.0, ISO 27001:2022, DORA, NIS2, PCI DSS v4.0 & more'
  },
  {
    icon: '🔒',
    title: 'No Cloud Access Needed',
    desc: 'Self-assessment checklist — nothing leaves your browser'
  },
  {
    icon: '📄',
    title: 'Downloadable Report',
    desc: 'Export your full posture report with remediation priorities'
  }
];

const frameworks = [
  'CSA CCM v4', 'ISO 27001:2022', 'NIST CSF 2.0',
  'CIS Controls v8', 'SOC 2 Type II', 'PCI DSS v4.0', 'DORA', 'NIS2'
];

const WelcomeModal = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(WELCOME_KEY);
    if (!seen) {
      // Small delay so the page renders first
      const t = setTimeout(() => setVisible(true), 350);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(WELCOME_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Overlay onClick={dismiss}>
      <Dialog onClick={e => e.stopPropagation()}>
        <Badge>2026 Edition · v3.0</Badge>

        <Title>Cloud Security Assessment Tool</Title>

        <Subtitle>
          Walk through a structured security checklist for your cloud environment.
          Get a scored posture report with prioritized recommendations — mapped to the
          compliance frameworks that matter to your organisation.
        </Subtitle>

        <FeatureGrid>
          {features.map(f => (
            <FeatureCard key={f.title}>
              <FeatureIcon>{f.icon}</FeatureIcon>
              <FeatureText>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureText>
            </FeatureCard>
          ))}
        </FeatureGrid>

        <div style={{ marginBottom: '10px', fontSize: '0.82em', color: '#6b7a9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Frameworks covered
        </div>
        <FrameworkRow>
          {frameworks.map(fw => (
            <FrameworkChip key={fw}>{fw}</FrameworkChip>
          ))}
        </FrameworkRow>

        <Actions>
          <StartButton onClick={dismiss}>
            Start Assessment →
          </StartButton>
          <DismissButton onClick={dismiss}>
            Don't show again
          </DismissButton>
        </Actions>

        <NoteRow>
          Built by <a href="https://quantumsecurity.ai/" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none' }}>QuantumSecurity.ai</a>
          {' '}·{' '}
          <a href="https://cisomarketplace.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none' }}>CISOMarketplace</a>
          {' '}· Members run free · credits / one-time guest pass · sign in to save your report
        </NoteRow>
      </Dialog>
    </Overlay>
  );
};

export default WelcomeModal;
